import { clx } from "@medusajs/ui"
import Image from "next/image"
import React from "react"

import PlaceholderImage from "@/modules/common/icons/placeholder-image"

type ThumbnailProps = {
  thumbnail?: string | null
  // TODO: Fix image typings
  images?: any[] | null
  alt: string
  size?: "small" | "medium" | "large" | "full" | "square"
  isFeatured?: boolean
  className?: string
  type?: "preview" | "full"
  /** Eager-load + high fetchpriority for above-the-fold images (LCP). */
  priority?: boolean
  "data-testid"?: string
}

const Thumbnail: React.FC<ThumbnailProps> = ({
  thumbnail,
  images,
  alt,
  size = "small",
  isFeatured,
  className,
  "data-testid": dataTestid,
  type,
  priority,
}) => {
  const initialImage = thumbnail || images?.[0]?.url

  return (
    <div
      className={clx("relative w-full overflow-hidden", className, {
        "aspect-[11/14]": isFeatured,
        "aspect-[9/16]": !isFeatured && size !== "square",
        "aspect-[1/1]": size === "square",
        "w-[180px]": size === "small",
        "w-[290px]": size === "medium",
        "w-[440px]": size === "large",
        "w-full": size === "full",
      })}
      data-testid={dataTestid}
    >
      <ImageOrPlaceholder
        image={initialImage}
        alt={alt}
        size={size}
        type={type}
        priority={priority}
      />
    </div>
  )
}

const ImageOrPlaceholder = ({
  image,
  alt,
  size,
  type,
  priority,
}: Pick<ThumbnailProps, "size" | "type" | "priority"> & {
  image?: string
  alt: string
}) => {
  return image ? (
    <Image
      src={image}
      alt={alt}
      className={clx("absolute inset-0 object-contain", {
        "p-4": type === "full",
        "p-2": type === "preview",
      })}
      draggable={false}
      quality={50}
      sizes="(max-width: 576px) 280px, (max-width: 768px) 360px, (max-width: 992px) 480px, 800px"
      fill
      priority={priority}
      // Explicit eager attr: inside a streamed Suspense boundary next/image's
      // preload link isn't hoisted, but this stops the first row lazy-loading.
      loading={priority ? "eager" : "lazy"}
    />
  ) : (
    <div className="w-full h-full absolute inset-0 flex items-center justify-center">
      <PlaceholderImage size={size === "small" ? 16 : 24} />
    </div>
  )
}

export default Thumbnail
